import { useState, useEffect } from 'react'

import { DndContext, closestCenter } from '@dnd-kit/core'
import { SortableContext, rectSwappingStrategy } from '@dnd-kit/sortable'
import _ from 'lodash'

import { useLevelPlaySurface } from '@/shared/level-play'

import { LevelPlayJigsawPiece } from './Piece'

export const LevelPlayJigsaw = ({ activity }) => {
  const { onComplete } = useLevelPlaySurface()
  const { meta } = activity
  const imageUrl = _.get(meta, 'image.file.url', undefined)
  const difficulty = _.get(meta, 'difficulty', 'hard')
  const [pieces, setPieces] = useState([])
  const [loadError, setLoadError] = useState(false)

  let numRows = 2
  let numCols = 2
  if (difficulty === 'medium') {
    numRows = 3
    numCols = 3
  } else if (difficulty === 'hard') {
    numRows = 4
    numCols = 3
  }

  useEffect(() => {
    let cancelled = false

    const failOpen = () => {
      if (cancelled) return
      setLoadError(true)
      // Fail open: never trap the player behind an unrenderable board.
      onComplete({
        uid: activity.uid,
        answer: `Quebra-cabeça indisponível (${difficulty}) — liberado automaticamente`
      })
    }

    setLoadError(false)
    setPieces([])

    if (!imageUrl) {
      failOpen()
      return () => {
        cancelled = true
      }
    }

    const numPieces = numRows * numCols
    const organizedPieces = Array.from({ length: numPieces }, (_, index) => ({
      position: index,
      id: index + 1
    }))

    const image = new Image()
    image.crossOrigin = 'Anonymous'

    image.onerror = failOpen

    image.onload = () => {
      if (cancelled) return
      try {
        // Recorta a fonte para um quadrado central (funciona com qualquer
        // proporção de imagem) e fatia esse quadrado na grade. Cada peça
        // carrega seu aspect real (pieceWidth/pieceHeight) para ser exibida
        // sem deformação — montada, a grade reconstrói o quadrado completo.
        const side = Math.min(image.width, image.height)
        const offsetX = (image.width - side) / 2
        const offsetY = (image.height - side) / 2
        const pieceWidth = side / numCols
        const pieceHeight = side / numRows
        const pieceAspectRatio = pieceWidth / pieceHeight

        const sliced = organizedPieces.map((piece, index) => {
          const canvas = document.createElement('canvas')
          const context = canvas.getContext('2d')

          const col = index % numCols
          const row = Math.floor(index / numCols)

          canvas.width = pieceWidth
          canvas.height = pieceHeight
          context.drawImage(
            image,
            offsetX + col * pieceWidth,
            offsetY + row * pieceHeight,
            pieceWidth,
            pieceHeight,
            0,
            0,
            pieceWidth,
            pieceHeight
          )

          return {
            ...piece,
            src: canvas.toDataURL(),
            aspectRatio: pieceAspectRatio
          }
        })

        setPieces(_.shuffle(sliced))
      } catch (error) {
        failOpen()
      }
    }

    image.src = imageUrl + '?not-from-cache-please'

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageUrl, numRows, numCols])

  const handleDragEnd = event => {
    const { active, over } = event

    if (active.id !== over.id) {
      setPieces(pieces => {
        const activeIndex = _.findIndex(pieces, piece => active.id === piece.id)
        const overIndex = _.findIndex(pieces, piece => over.id === piece.id)

        const updatedPieces = _.cloneDeep(pieces)

        ;[updatedPieces[activeIndex], updatedPieces[overIndex]] = [
          updatedPieces[overIndex],
          updatedPieces[activeIndex]
        ]

        const positions = _.map(updatedPieces, 'position')
        const expectedPositions = _.range(updatedPieces.length)
        const isEqualPositions = _.isEqual(expectedPositions, positions)

        if (isEqualPositions)
          onComplete({
            uid: activity.uid,
            answer: `Montou o puzzle de dificuldade ${difficulty}`
          })
        return updatedPieces
      })
    }
  }

  return (
    <div className="flex flex-col items-center gap-4 w-full">
      <p className="text-sm text-gray-700 text-center max-w-[400px]">
        Monte o quebra-cabeça arrastando as peças para a ordem correta.
      </p>

      {imageUrl && !loadError && (
        <img
          src={imageUrl}
          alt="Imagem de referência do quebra-cabeça"
          className="w-20 h-20 object-cover rounded-lg border border-gray-200 shadow-sm opacity-90"
        />
      )}

      {loadError ? (
        <p className="text-sm text-amber-600 text-center max-w-[400px]">
          Não foi possível carregar o quebra-cabeça. Você pode continuar.
        </p>
      ) : (
        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={pieces} strategy={rectSwappingStrategy}>
            <div
              className="max-w-[400px] mx-auto p-2 bg-gray-50 rounded-xl border border-gray-200"
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${numCols}, 1fr)`,
                gap: '6px',
                touchAction: 'none'
              }}
            >
              {pieces.map(piece => (
                <LevelPlayJigsawPiece
                  id={piece.id}
                  key={piece.id}
                  imageSrc={piece.src}
                  position={piece.position}
                  aspectRatio={piece.aspectRatio}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  )
}
